import { z } from "zod";
import { BusinessDate, EntityBase, Instant, Name, Note } from "./common.js";
import { Id } from "./ids.js";
import { PrepStation } from "./menu.js";
import { LocalTime, windowInstant } from "./orders.js";

/**
 * Production contracts (slice 2: architecture §8.3, §8.5 / ADR-22, requirements §7, DL-050…062). The manager splits
 * work between kitchens every day; the system computes start and transport times backwards from «آماده شود تا»
 * and keeps every kitchen informed. Planning itself is the pure `@kitchen/planning` engine; these are the stored
 * and transported shapes around it.
 */

// ---------------------------------------------------------------------------------------------------------------
// Settings the manager changes rarely (F4-T03)

const Minutes = z.int().min(1).max(1440);
const Portions = z.int().min(1).max(100_000);

/** Per kitchen site: when the day starts, how long each component cooks there and how much one pot holds. */
export const KitchenProfile = z.object({
  siteId: Id,
  /** null = nothing stored yet (defaults shown); required to replace, like companion packages. */
  version: z.int().nonnegative().nullable(),
  dayStart: LocalTime,
  /** Used when a component has no own duration; shown as a warning so the manager fills it in. */
  defaultCookMinutes: Minutes,
  cookMinutes: z.record(Id, Minutes),
  /** Portions per pot (ظرف پخت, req §7.2); missing = one pot whatever the count. */
  potCapacity: z.record(Id, Portions),
});
export type KitchenProfile = z.infer<typeof KitchenProfile>;
export const KitchenProfileInput = KitchenProfile.omit({ siteId: true });
export type KitchenProfileInput = z.infer<typeof KitchenProfileInput>;

/** Real travel time between two sites, set by the manager (DL-054). One direction is enough; the reverse is used. */
export const TravelTime = z
  .object({ fromSiteId: Id, toSiteId: Id, minutes: z.int().min(0).max(600) })
  .refine((t) => t.fromSiteId !== t.toSiteId, { message: "a site has no travel time to itself" });
export type TravelTime = z.infer<typeof TravelTime>;

/** Packing: where and when (DL-052). The driver loads at the packing site. */
export const PackingPlace = z.object({ siteId: Id, packAt: LocalTime });
export type PackingPlace = z.infer<typeof PackingPlace>;

/** The usual times of a meal (DL-053); every one is changed per day in the plan decisions. */
export const MealTimes = z.object({
  mealId: Id,
  /** null = not set yet: the plan of that meal asks the manager for its times. */
  version: z.int().nonnegative().nullable(),
  readyBy: LocalTime,
  handoverBy: LocalTime,
  packing: PackingPlace,
});
export type MealTimes = z.infer<typeof MealTimes>;

// ---------------------------------------------------------------------------------------------------------------
// The manager's daily decisions for one (business date, meal) (F4-T04)

export const KitchenSplit = z.object({ kitchenId: Id, portions: Portions });
export type KitchenSplit = z.infer<typeof KitchenSplit>;

/** Whole food to one kitchen, or one component split between kitchens (DL-050). A component split wins. */
export const PlanAssignment = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("food"), foodItemId: Id, kitchenId: Id }),
  z.object({
    kind: z.literal("component"),
    componentId: Id,
    splits: z
      .array(KitchenSplit)
      .min(1)
      .max(10)
      .refine((s) => new Set(s.map((x) => x.kitchenId)).size === s.length, {
        message: "a kitchen appears once per split",
      }),
  }),
]);
export type PlanAssignment = z.infer<typeof PlanAssignment>;

export const PlanDecisions = z.object({
  version: z.int().nonnegative(),
  assignments: z.array(PlanAssignment).max(200),
  readyBy: LocalTime,
  handoverBy: LocalTime,
  packing: PackingPlace,
  /** Per-food packing place, e.g. «برنج را ببر تالار». */
  packingByFood: z.array(PackingPlace.extend({ foodItemId: Id })).max(100),
  /** Per-component «آماده شود تا», earlier or later than the meal's. */
  readyByComponent: z.array(z.object({ componentId: Id, readyBy: LocalTime })).max(100),
  note: Note,
});
export type PlanDecisions = z.infer<typeof PlanDecisions>;

/** Confirming publishes the plan to the kitchens and queues their slips; it can be re-confirmed after changes. */
export const PlanConfirm = z.object({ version: z.int().nonnegative() });

// ---------------------------------------------------------------------------------------------------------------
// Kitchen tasks (F4-T05)

export const TaskStatus = z.enum(["planned", "started", "ready", "cancelled"]);
export type TaskStatus = z.infer<typeof TaskStatus>;

/**
 * A task is cancelled when its kitchen no longer cooks that component for the meal (demand gone or moved); it comes
 * back as planned if the work returns. A ready task is final: later increases make a new task (AS-037).
 */
export const TASK_TRANSITIONS: Readonly<Record<TaskStatus, readonly TaskStatus[]>> = {
  planned: ["started", "cancelled"],
  started: ["ready", "cancelled"],
  ready: [],
  cancelled: ["planned"],
};

export function canMoveTask(from: TaskStatus, to: TaskStatus): boolean {
  return TASK_TRANSITIONS[from].includes(to);
}

export const KitchenTask = EntityBase.extend({
  businessDate: BusinessDate,
  mealId: Id,
  componentId: Id,
  kitchenId: Id,
  portions: z.int().min(0).max(100_000),
  pots: z.int().min(1).max(1000),
  potCapacity: Portions.nullable(),
  cookMinutes: Minutes,
  startBy: LocalTime,
  readyBy: LocalTime,
  status: TaskStatus,
  startedAt: Instant.nullable(),
  readyAt: Instant.nullable(),
  /** Portions when cooking started; differs from `portions` when an order changed after the start (urgent). */
  portionsAtStart: z.int().min(0).nullable(),
  /** Real production on «تولید انجام شد» (req §7.2): fewer → manager alert, more → surplus. */
  actualPortions: z.int().min(0).nullable(),
});
export type KitchenTask = z.infer<typeof KitchenTask>;

export const TaskStart = z.object({ version: z.int().nonnegative() });
export const TaskReady = z.object({
  version: z.int().nonnegative(),
  actualPortions: z.int().min(0).max(100_000),
});
/** «انتقال کار به آشپزخانه‌ی دیگر» (architecture §8.5 د): one action, both kitchens told. */
export const TaskMove = z.object({ version: z.int().nonnegative(), toKitchenId: Id, reason: Note.min(1) });

// ---------------------------------------------------------------------------------------------------------------
// Food transfers between kitchens (F4-T06): a document like a stock transfer (DL-052, DL-073)

export const TransferStatus = z.enum(["planned", "sent", "received", "cancelled"]);
export type TransferStatus = z.infer<typeof TransferStatus>;

export const TRANSFER_TRANSITIONS: Readonly<Record<TransferStatus, readonly TransferStatus[]>> = {
  planned: ["sent", "cancelled"],
  sent: ["received"],
  received: [],
  cancelled: ["planned"],
};

export function canMoveTransfer(from: TransferStatus, to: TransferStatus): boolean {
  return TRANSFER_TRANSITIONS[from].includes(to);
}

export const FoodTransfer = EntityBase.extend({
  businessDate: BusinessDate,
  mealId: Id,
  componentId: Id,
  fromKitchenId: Id,
  toSiteId: Id,
  portions: z.int().min(0).max(100_000),
  travelMinutes: z.int().min(0).max(600),
  departBy: LocalTime,
  arriveBy: LocalTime,
  status: TransferStatus,
  sentPortions: z.int().min(0).nullable(),
  sentAt: Instant.nullable(),
  driverPersonId: Id.nullable(),
  receivedPortions: z.int().min(0).nullable(),
  receivedAt: Instant.nullable(),
});
export type FoodTransfer = z.infer<typeof FoodTransfer>;

export const TransferSend = z.object({
  version: z.int().nonnegative(),
  portions: z.int().min(0).max(100_000),
  driverPersonId: Id.nullable(),
});
/** Simple receipt: one tap with the count, no signature (DL-073). A different count is reported, not refused. */
export const TransferReceive = z.object({
  version: z.int().nonnegative(),
  portions: z.int().min(0).max(100_000),
});

// ---------------------------------------------------------------------------------------------------------------
// Countdown (architecture §8.5 ج 4): green / yellow / red, pure so the board, alerts and tests agree

export const Countdown = z.enum(["done", "green", "yellow", "red"]);
export type Countdown = z.infer<typeof Countdown>;

const minutesOf = (t: LocalTime) => {
  const [h, m] = t.split(":").map(Number) as [number, number];
  return h * 60 + m;
};

/** Minutes since 00:00 Tehran on `businessDate`; negative the day before, above 1440 the day after. */
export function minutesIntoBusinessDate(now: Date, businessDate: BusinessDate): number {
  const midnight = windowInstant(businessDate, { daysBefore: 0, time: "00:00" });
  return Math.floor((now.getTime() - midnight.getTime()) / 60_000);
}

/**
 * planned: red once start-by has passed, yellow inside the warning window before it.
 * started: red once ready-by has passed, yellow inside the warning window before it. ready / cancelled: done.
 */
export function taskCountdown(
  task: { status: TaskStatus; startBy: LocalTime; readyBy: LocalTime },
  nowMinutes: number,
  warnMinutes: number,
): Countdown {
  if (task.status === "ready" || task.status === "cancelled") return "done";
  const due = minutesOf(task.status === "planned" ? task.startBy : task.readyBy);
  if (nowMinutes >= due) return "red";
  if (nowMinutes >= due - warnMinutes) return "yellow";
  return "green";
}

/** A transfer is late once its depart-by has passed and it has not left. */
export function transferCountdown(
  transfer: { status: TransferStatus; departBy: LocalTime },
  nowMinutes: number,
  warnMinutes: number,
): Countdown {
  if (transfer.status !== "planned") return "done";
  const due = minutesOf(transfer.departBy);
  if (nowMinutes >= due) return "red";
  if (nowMinutes >= due - warnMinutes) return "yellow";
  return "green";
}

// ---------------------------------------------------------------------------------------------------------------
// Board and station views (F4-T07). Each kitchen sees only its own work (architecture §8.5 ج 3).

export const PlanWarning = z.object({
  code: z.string(),
  severity: z.enum(["normal", "important", "urgent"]),
  message: z.string(),
  componentId: z.string().optional(),
  kitchenId: z.string().optional(),
});
export type PlanWarning = z.infer<typeof PlanWarning>;

export const BoardTask = z.object({
  id: Id,
  version: z.int().nonnegative(),
  kitchenId: Id,
  componentId: Id,
  componentName: Name,
  portions: z.int().min(0),
  pots: z.int().min(1),
  potCapacity: z.int().nullable(),
  startBy: LocalTime,
  readyBy: LocalTime,
  status: TaskStatus,
  countdown: Countdown,
  portionsAtStart: z.int().nullable(),
  actualPortions: z.int().nullable(),
});
export type BoardTask = z.infer<typeof BoardTask>;

/** One order line as the kitchen sees it: food, tier, count and remarks. No price, no special portions. */
export const KitchenLine = z.strictObject({
  foodItemId: Id,
  foodName: Name,
  tierId: Id.nullable(),
  tierName: z.string(),
  quantity: z.int().min(0),
  note: z.string(),
});
export type KitchenLine = z.infer<typeof KitchenLine>;

export const KitchenUnitOrder = z.strictObject({
  orderId: Id,
  unitId: Id,
  unitName: Name,
  organizationId: Id,
  organizationName: Name,
  contactNote: z.string(),
  kitchenNote: z.string(),
  lines: z.array(KitchenLine),
});
export type KitchenUnitOrder = z.infer<typeof KitchenUnitOrder>;

/** Head chef board, both views (DL-056): pots by food (tasks) and orders by organization (units). */
export const KitchenBoardView = z.object({
  siteId: Id,
  siteName: Name,
  businessDate: BusinessDate,
  mealId: Id,
  mealName: Name,
  /** False until the manager confirms the plan; the board shows it as a draft. */
  confirmed: z.boolean(),
  tasks: z.array(BoardTask),
  transfersOut: z.array(FoodTransfer),
  transfersIn: z.array(FoodTransfer),
  orders: z.array(KitchenUnitOrder),
  /** Submitted but not yet approved portions of the meal: shown, never planned (AS-036). */
  pendingPortions: z.int().min(0),
});
export type KitchenBoardView = z.infer<typeof KitchenBoardView>;

/** Bread or companion station: totals per item and per organization, units rounded up from thousandths. */
export const PrepStationView = z.object({
  siteId: Id,
  businessDate: BusinessDate,
  mealId: Id,
  station: PrepStation,
  items: z.array(
    z.object({
      itemId: Id,
      itemName: Name,
      total: z.int().min(0),
      /** `people`: portions that bring this item (for the bread ratio on the printed sheet). */
      byOrganization: z.array(
        z.object({
          organizationId: Id,
          organizationName: Name,
          units: z.int().min(0),
          people: z.int().min(0),
        }),
      ),
    }),
  ),
  ready: z.array(Id),
});
export type PrepStationView = z.infer<typeof PrepStationView>;

/** Packing line for staff without `orders.upgrade` (view): a strict object, so no special-portion field fits in. */
export const PackingLine = z.strictObject({
  foodItemId: Id,
  foodName: Name,
  tierId: Id.nullable(),
  tierName: z.string(),
  quantity: z.int().min(0),
});
export type PackingLine = z.infer<typeof PackingLine>;

/** Packing line for staff with `orders.upgrade` (view) (architecture §7.8): how many are packed as special. */
export const PackingLineWithSpecial = PackingLine.extend({ special: z.int().min(0) });
export type PackingLineWithSpecial = z.infer<typeof PackingLineWithSpecial>;

export const PackingUnit = <L extends z.ZodType>(line: L) =>
  z.strictObject({ orderId: Id, unitId: Id, unitName: Name, lines: z.array(line) });

export const PackingStationView = <L extends z.ZodType>(line: L) =>
  z.object({
    siteId: Id,
    businessDate: BusinessDate,
    mealId: Id,
    packAt: LocalTime,
    organizations: z.array(
      z.object({ organizationId: Id, organizationName: Name, units: z.array(PackingUnit(line)) }),
    ),
    ready: z.array(Id),
  });
export const PackingView = PackingStationView(PackingLine);
export type PackingView = z.infer<typeof PackingView>;
export const PackingViewWithSpecial = PackingStationView(PackingLineWithSpecial);
export type PackingViewWithSpecial = z.infer<typeof PackingViewWithSpecial>;

/** Station readiness tick per organization (req §7.3–§7.4 «تیک آماده»). */
export const StationTick = z.object({
  station: z.enum(["bread", "companions", "packing"]),
  organizationId: Id,
  ready: z.boolean(),
});
export type StationTick = z.infer<typeof StationTick>;

// ---------------------------------------------------------------------------------------------------------------
// Events (architecture §8.2). Payloads carry ids; consumers read what they need.

export const PRODUCTION_EVENTS = [
  "production.plan-confirmed",
  "production.task-changed",
  "production.task-started",
  "production.task-ready",
  "production.task-moved",
  "production.transfer-sent",
  "production.transfer-received",
] as const;
export type ProductionEvent = (typeof PRODUCTION_EVENTS)[number];

export const ProductionEventPayload = z.object({
  businessDate: BusinessDate,
  mealId: Id,
  kitchenIds: z.array(Id),
  taskId: Id.optional(),
  transferId: Id.optional(),
  /** Changes to a started task are urgent (architecture §8.5 د). */
  urgent: z.boolean(),
  /** For `production.task-changed`: portions before and after. */
  before: z.int().min(0).optional(),
  after: z.int().min(0).optional(),
});
export type ProductionEventPayload = z.infer<typeof ProductionEventPayload>;
